import { useIntl } from "react-intl";
import type { FieldOption } from "@/lib/field-util";
import { SearchableSelect } from "./SearchableSelect";

interface DegreeSelectProps {
  value: string | null | undefined;
  onChange: (next: string) => void;
  options: readonly FieldOption[];
  placeholder?: string;
  className?: string;
  editable?: boolean;
  variant?: "box" | "plain";
  disabled?: boolean;
  required?: boolean;
  id?: string;
  ariaLabel?: string;
}

/**
 * A catalogue-specific single select. The catalogue is admin-maintained, so this
 * wrapper deliberately applies no business hierarchy. It only sorts the current
 * labels for a predictable, searchable presentation.
 */
export function DegreeSelect({
  value,
  onChange,
  options,
  placeholder,
  className = "",
  editable = true,
  variant = "box",
  disabled = false,
  required = false,
  id,
  ariaLabel,
}: DegreeSelectProps) {
  const intl = useIntl();
  const fallbackPlaceholder =
    placeholder ??
    intl.formatMessage({
      id: "profile.page.field.degree.placeholder",
      defaultMessage: "Choose degree",
    });
  const collator = new Intl.Collator(intl.locale, { sensitivity: "base" });
  const sortedOptions = options
    .map(({ value: optionValue, label }) => ({ value: optionValue, label }))
    .sort((left, right) => collator.compare(left.label, right.label));
  return (
    <SearchableSelect
      id={id}
      ariaLabel={ariaLabel}
      value={value}
      onChange={onChange}
      options={sortedOptions}
      placeholder={fallbackPlaceholder}
      searchAriaLabel={intl.formatMessage({
        id: "profile.page.field.degree.search",
        defaultMessage: "Search degree",
      })}
      searchPlaceholder={intl.formatMessage({
        id: "design.filterPlaceholder",
        defaultMessage: "Type to filter …",
      })}
      noResultsLabel={intl.formatMessage({
        id: "search.noResults",
        defaultMessage: "No matches",
      })}
      emptyOptionLabel={intl.formatMessage({
        id: "profile.page.field.degree.none",
        defaultMessage: "No degree selected",
      })}
      className={className}
      editable={editable}
      variant={variant}
      disabled={disabled}
      required={required}
    />
  );
}
